import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ProfileLayout } from '@/components/layout/ProfileLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { teacherPricesApi } from '@/api/teacherPrices'
import { lessonSubscriptionsApi } from '@/api/lessonSubscriptions'
import type { LessonSubscriptionPlan, LessonSubscriptionType, TeacherLessonPrice } from '@/types/billing'
import { formatMoney } from '@/components/billing/formatMoney'
import { getErrorMessage } from '@/utils/error'
import '@/styles/dashboard.css'

export function TeacherBillingPage() {
  const { t } = useTranslation()
  const [activePrice, setActivePrice] = useState<TeacherLessonPrice | null>(null)
  const [priceHistory, setPriceHistory] = useState<TeacherLessonPrice[]>([])
  const [plans, setPlans] = useState<LessonSubscriptionPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [priceDialogOpen, setPriceDialogOpen] = useState(false)
  const [planDialogOpen, setPlanDialogOpen] = useState(false)
  const [newPrice, setNewPrice] = useState('')
  const [newEffectiveFrom, setNewEffectiveFrom] = useState('')
  const [planForm, setPlanForm] = useState({
    title: '',
    subscription_type: 'individual' as LessonSubscriptionType,
    lessons_count: '8',
    duration_days: '30',
    total_price: '',
    description: '',
  })
  const [deletePlanId, setDeletePlanId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [prices, plansRes] = await Promise.all([
        teacherPricesApi.index(),
        lessonSubscriptionsApi.listPlans({ per_page: 100 }),
      ])
      setActivePrice(prices.active)
      setPriceHistory(prices.history)
      setPlans(plansRes.data)
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    load().catch(() => {})
  }, [load])

  const handleSavePrice = async () => {
    const amount = parseFloat(newPrice)
    if (!Number.isFinite(amount)) return
    setSubmitting(true)
    try {
      await teacherPricesApi.store({
        price_per_hour: amount,
        effective_from: newEffectiveFrom || undefined,
      })
      setPriceDialogOpen(false)
      setNewPrice('')
      await load()
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleSavePlan = async () => {
    const lessonsCount = parseInt(planForm.lessons_count, 10)
    const totalPrice = parseFloat(planForm.total_price)
    if (!Number.isFinite(lessonsCount) || lessonsCount <= 0 || !Number.isFinite(totalPrice)) return

    const pricePerLesson = totalPrice / lessonsCount

    setSubmitting(true)
    try {
      await lessonSubscriptionsApi.createPlan({
        title: planForm.title,
        subscription_type: planForm.subscription_type,
        lessons_count: lessonsCount,
        duration_days: parseInt(planForm.duration_days, 10),
        price_per_lesson: pricePerLesson,
        currency: 'RUB',
        description: planForm.description || null,
      })
      setPlanDialogOpen(false)
      setPlanForm((p) => ({ ...p, total_price: '' }))
      await load()
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeletePlan = async () => {
    if (deletePlanId === null) return
    setSubmitting(true)
    try {
      await lessonSubscriptionsApi.deletePlan(deletePlanId)
      setDeletePlanId(null)
      await load()
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
      setDeletePlanId(null)
    } finally {
      setSubmitting(false)
    }
  }

  const togglePlanActive = async (plan: LessonSubscriptionPlan) => {
    try {
      await lessonSubscriptionsApi.updatePlan(plan.id, { is_active: !plan.is_active })
      await load()
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    }
  }

  return (
    <ProfileLayout>
      <Box sx={{ p: 3, pt: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('billing.teacherPageTitle', 'Тарифы и абонементы')}
          </Typography>
          <PageHelpPopover pageKey="profile-billing" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <LinearProgress />
        ) : (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">{t('billing.basePrice', 'Базовая цена за час')}</Typography>
                  <Button size="small" variant="outlined" onClick={() => setPriceDialogOpen(true)}>
                    {t('billing.setPrice', 'Установить цену')}
                  </Button>
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {activePrice
                    ? `${formatMoney(activePrice.price_per_hour, activePrice.currency)}/ч`
                    : t('billing.noBasePrice', 'Не задана')}
                </Typography>
                {priceHistory.length > 0 && (
                  <Table size="small" sx={{ mt: 2 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('billing.effectiveFrom', 'С')}</TableCell>
                        <TableCell>{t('billing.amount', 'Сумма')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {priceHistory.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>{p.effective_from}</TableCell>
                          <TableCell>{formatMoney(p.price_per_hour, p.currency)}/ч</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">{t('billing.plansCatalog', 'Каталог абонементов')}</Typography>
                  <Button size="small" variant="outlined" onClick={() => setPlanDialogOpen(true)}>
                    {t('billing.addPlan', 'Добавить абонемент')}
                  </Button>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('billing.plan', 'Абонемент')}</TableCell>
                      <TableCell>{t('billing.planType', 'Тип')}</TableCell>
                      <TableCell>{t('billing.lessonsCount', 'Уроков')}</TableCell>
                      <TableCell>{t('billing.totalPrice', 'Общая стоимость')}</TableCell>
                      <TableCell>{t('billing.pricePerLesson', 'Цена/урок')}</TableCell>
                      <TableCell>{t('billing.active', 'Активен')}</TableCell>
                      <TableCell align="right">{t('common.actions', 'Действия')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plans.map((plan) => (
                      <TableRow key={plan.id}>
                        <TableCell>{plan.title}</TableCell>
                        <TableCell>{t(`billing.subscriptionType.${plan.subscription_type}`, plan.subscription_type)}</TableCell>
                        <TableCell>{plan.lessons_count}</TableCell>
                        <TableCell>
                          {plan.total_price != null
                            ? formatMoney(plan.total_price, plan.currency)
                            : formatMoney(Number(plan.price_per_lesson) * plan.lessons_count, plan.currency)}
                        </TableCell>
                        <TableCell>{formatMoney(plan.price_per_lesson, plan.currency)}</TableCell>
                        <TableCell>
                          <Switch
                            checked={plan.is_active}
                            onChange={() => void togglePlanActive(plan)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="error"
                            onClick={() => setDeletePlanId(plan.id)}
                          >
                            {t('common.delete', 'Удалить')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}
      </Box>

      <Dialog open={priceDialogOpen} onClose={() => setPriceDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('billing.setPrice', 'Установить цену')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              size="small"
              type="number"
              label={t('billing.pricePerHour', 'Цена за час')}
              value={newPrice}
              onChange={(e) => setNewPrice(e.target.value)}
            />
            <TextField
              size="small"
              type="date"
              label={t('billing.effectiveFrom', 'Действует с')}
              InputLabelProps={{ shrink: true }}
              value={newEffectiveFrom}
              onChange={(e) => setNewEffectiveFrom(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceDialogOpen(false)}>{t('common.cancel', 'Отмена')}</Button>
          <Button variant="contained" disabled={submitting} onClick={() => void handleSavePrice()}>
            {t('common.save', 'Сохранить')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={planDialogOpen} onClose={() => setPlanDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('billing.addPlan', 'Добавить абонемент')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              size="small"
              label={t('billing.planTitle', 'Название')}
              value={planForm.title}
              onChange={(e) => setPlanForm((p) => ({ ...p, title: e.target.value }))}
            />
            <FormControl size="small">
              <InputLabel>{t('billing.planType', 'Тип')}</InputLabel>
              <Select
                label={t('billing.planType', 'Тип')}
                value={planForm.subscription_type}
                onChange={(e) =>
                  setPlanForm((p) => ({ ...p, subscription_type: e.target.value as LessonSubscriptionType }))
                }
              >
                <MenuItem value="individual">{t('billing.subscriptionType.individual', 'Индивидуальный')}</MenuItem>
                <MenuItem value="group">{t('billing.subscriptionType.group', 'Групповой')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="number"
              label={t('billing.lessonsCount', 'Количество уроков')}
              value={planForm.lessons_count}
              onChange={(e) => setPlanForm((p) => ({ ...p, lessons_count: e.target.value }))}
            />
            <TextField
              size="small"
              type="number"
              label={t('billing.durationDays', 'Срок (дней)')}
              value={planForm.duration_days}
              onChange={(e) => setPlanForm((p) => ({ ...p, duration_days: e.target.value }))}
            />
            <TextField
              size="small"
              type="number"
              label={t('billing.totalPrice', 'Общая стоимость абонемента')}
              value={planForm.total_price}
              onChange={(e) => setPlanForm((p) => ({ ...p, total_price: e.target.value }))}
            />
            <TextField
              size="small"
              type="number"
              label={t('billing.pricePerLesson', 'Цена за урок')}
              value={
                planForm.total_price && planForm.lessons_count
                  ? (parseFloat(planForm.total_price) / parseInt(planForm.lessons_count, 10)).toFixed(2)
                  : ''
              }
              InputProps={{ readOnly: true }}
              helperText={t('billing.pricePerLessonCalculated', 'Рассчитывается автоматически')}
            />
            <TextField
              size="small"
              multiline
              minRows={2}
              label={t('billing.description', 'Описание')}
              value={planForm.description}
              onChange={(e) => setPlanForm((p) => ({ ...p, description: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialogOpen(false)}>{t('common.cancel', 'Отмена')}</Button>
          <Button variant="contained" disabled={submitting} onClick={() => void handleSavePlan()}>
            {t('common.save', 'Сохранить')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deletePlanId !== null} onClose={() => setDeletePlanId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('billing.deletePlanTitle', 'Удалить абонемент?')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('billing.deletePlanConfirm', 'Тариф будет удалён из каталога. Действие необратимо.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePlanId(null)}>{t('common.cancel', 'Отмена')}</Button>
          <Button variant="contained" color="error" disabled={submitting} onClick={() => void handleDeletePlan()}>
            {t('common.delete', 'Удалить')}
          </Button>
        </DialogActions>
      </Dialog>
    </ProfileLayout>
  )
}
